// ============================================================
// profile.ts — Player profile & stats screen
// DOM-based component, dark military theme.
// ============================================================

import { createRankBadge, getRankTier } from './rank-badge';

const ACCENT = '#e94560';
const BG_DARK = '#1a1a2e';
const BG_PANEL = '#16213e';

// ── Types ───────────────────────────────────────────────────

export interface WeaponStat {
  weapon: string;
  kills: number;
  headshotPct: number; // 0-100
  accuracy: number;    // 0-100
}

export interface RecentMatch {
  map: string;
  result: 'win' | 'loss' | 'draw';
  kills: number;
  deaths: number;
  assists: number;
  ratingChange: number;
}

export interface Achievement {
  id: string;
  name: string;
  icon: string;
  unlocked: boolean;
}

export interface ProfileData {
  displayName: string;
  rating: number;
  kills: number;
  deaths: number;
  hltvRating: number;    // HLTV 2.0 rating
  adr: number;           // average damage per round
  winRate: number;        // 0-100
  wins: number;
  losses: number;
  ratingHistory: number[]; // last 20 match ratings
  weaponStats: WeaponStat[];
  recentMatches: RecentMatch[];
  achievements?: Achievement[];
}

// ── State ───────────────────────────────────────────────────

let overlay: HTMLElement | null = null;
let ratingChartResizeObserver: ResizeObserver | null = null;
let currentProfileData: ProfileData | null = null;

const DEFAULT_ACHIEVEMENTS: Achievement[] = [
  { id: 'ace', name: 'Ace Round', icon: 'A', unlocked: true },
  { id: 'defuser', name: 'Clutch Defuse', icon: 'D', unlocked: true },
  { id: 'sharpshooter', name: 'Sharpshooter', icon: 'H', unlocked: false },
  { id: 'entry', name: 'Entry Fragger', icon: 'E', unlocked: true },
  { id: 'economist', name: 'Eco Specialist', icon: '$', unlocked: false },
];
let profileAchievements: Achievement[] = DEFAULT_ACHIEVEMENTS;

// ── Style injection ─────────────────────────────────────────

let stylesInjected = false;

function injectStyles(): void {
  if (stylesInjected) return;
  stylesInjected = true;

  const style = document.createElement('style');
  style.textContent = `
    #profile-overlay {
      position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
      background: rgba(0,0,0,0.92);
      z-index: 140;
      display: none;
      font-family: 'Courier New', monospace;
      color: #ffffff;
      user-select: none;
      overflow-y: auto;
    }

    .prof-container {
      max-width: 800px;
      margin: 0 auto;
      padding: 40px 20px 60px;
    }

    .prof-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 30px;
    }

    .prof-identity {
      display: flex;
      align-items: center;
      gap: 16px;
    }

    .prof-name {
      font-size: 28px; font-weight: bold;
      color: #fff; letter-spacing: 2px;
    }

    .prof-close-btn {
      font-size: 24px; color: #666;
      cursor: pointer; padding: 4px 10px;
      transition: color 0.15s;
    }
    .prof-close-btn:hover { color: #fff; }

    /* Stats grid */
    .prof-stats-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
      gap: 12px;
      margin-bottom: 30px;
    }

    .prof-stat-card {
      background: linear-gradient(135deg, ${BG_PANEL}, ${BG_DARK});
      border: 1px solid #222;
      border-radius: 4px;
      padding: 16px;
      text-align: center;
    }

    .prof-stat-label {
      font-size: 10px; color: #555;
      letter-spacing: 2px; text-transform: uppercase;
      margin-bottom: 6px;
    }

    .prof-stat-value {
      font-size: 24px; font-weight: bold;
      color: #fff; letter-spacing: 1px;
    }

    .prof-stat-sub {
      font-size: 11px; color: #444;
      margin-top: 4px;
    }

    /* Achievements */
    .prof-achievement-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
      gap: 10px;
      margin-bottom: 30px;
    }

    .prof-achievement-card {
      display: grid;
      grid-template-columns: 34px 1fr;
      gap: 10px;
      align-items: center;
      padding: 10px;
      background: rgba(20, 22, 36, 0.8);
      border: 1px solid #222;
      border-radius: 4px;
    }

    .prof-achievement-card.locked {
      opacity: 0.52;
      filter: grayscale(0.8);
    }

    .prof-achievement-icon {
      width: 34px; height: 34px;
      display: flex; align-items: center; justify-content: center;
      border-radius: 4px;
      border: 1px solid rgba(233,69,96,0.35);
      color: ${ACCENT};
      background: rgba(233,69,96,0.08);
      font-size: 16px; font-weight: bold;
    }

    .prof-achievement-name {
      font-size: 11px; color: #ddd;
      font-weight: bold;
      letter-spacing: 1px;
    }

    .prof-achievement-status {
      font-size: 9px;
      margin-top: 3px;
      letter-spacing: 1px;
      text-transform: uppercase;
      color: #777;
    }

    .prof-achievement-status.unlocked { color: #44ff44; }

    /* K/D bar */
    .prof-kd-bar {
      height: 6px; border-radius: 3px;
      background: #ff4444; margin-top: 8px;
      overflow: hidden;
    }
    .prof-kd-bar-fill {
      height: 100%; background: #44ff44;
      border-radius: 3px;
    }

    /* Win rate circle */
    .prof-winrate-circle {
      width: 60px; height: 60px;
      margin: 0 auto 6px;
      position: relative;
    }
    .prof-winrate-circle canvas {
      width: 60px; height: 60px;
    }
    .prof-winrate-text {
      position: absolute;
      top: 50%; left: 50%;
      transform: translate(-50%, -50%);
      font-size: 14px; font-weight: bold; color: #fff;
    }

    /* Section titles */
    .prof-section-title {
      font-size: 14px; font-weight: bold;
      color: ${ACCENT}; letter-spacing: 3px;
      text-transform: uppercase;
      margin-bottom: 12px;
      padding-bottom: 6px;
      border-bottom: 1px solid rgba(233,69,96,0.3);
    }

    /* Rating chart */
    .prof-chart-wrap {
      background: linear-gradient(135deg, ${BG_PANEL}, ${BG_DARK});
      border: 1px solid #222;
      border-radius: 4px;
      padding: 16px;
      margin-bottom: 30px;
    }
    .prof-chart-wrap canvas {
      width: 100%; height: 150px;
      display: block;
    }

    /* Weapon stats table */
    .prof-weapon-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 12px;
      margin-bottom: 30px;
    }
    .prof-weapon-table th {
      text-align: left; padding: 8px 6px;
      color: #555; font-weight: bold;
      letter-spacing: 1px; text-transform: uppercase;
      font-size: 10px;
      border-bottom: 1px solid #333;
    }
    .prof-weapon-table th.center { text-align: center; }
    .prof-weapon-table td {
      padding: 7px 6px; color: #bbb;
      border-bottom: 1px solid #1a1a2a;
    }
    .prof-weapon-table td.center { text-align: center; }
    .prof-weapon-table tr:hover td {
      background: rgba(233,69,96,0.06);
    }

    /* Recent matches */
    .prof-match-list {
      display: flex; flex-direction: column;
      gap: 6px;
    }

    .prof-match-row {
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 10px 14px;
      background: rgba(20, 22, 36, 0.8);
      border: 1px solid #222;
      border-radius: 3px;
      font-size: 12px;
    }

    .prof-match-result {
      font-weight: bold;
      letter-spacing: 2px;
      text-transform: uppercase;
      width: 50px;
      text-align: center;
    }
    .prof-match-result.win { color: #44ff44; }
    .prof-match-result.loss { color: #ff4444; }
    .prof-match-result.draw { color: #888; }

    .prof-match-map {
      color: #888; width: 80px;
      letter-spacing: 1px;
    }

    .prof-match-kda {
      color: #ccc; flex: 1;
      letter-spacing: 1px;
    }

    .prof-match-rating {
      font-weight: bold;
      letter-spacing: 1px;
    }
    .prof-match-rating.positive { color: #44ff44; }
    .prof-match-rating.negative { color: #ff4444; }
  `;
  document.head.appendChild(style);
}

// ── Helpers ─────────────────────────────────────────────────

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function ensureOverlay(): HTMLElement {
  if (overlay) return overlay;
  injectStyles();
  overlay = document.createElement('div');
  overlay.id = 'profile-overlay';
  document.body.appendChild(overlay);
  return overlay;
}

function cleanupRatingChartObserver(): void {
  ratingChartResizeObserver?.disconnect();
  ratingChartResizeObserver = null;
}

/** Draw a circular progress indicator on a canvas. */
function drawWinRateCircle(canvas: HTMLCanvasElement, pct: number): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const size = 120; // render at 2x for clarity
  canvas.width = size;
  canvas.height = size;

  const cx = size / 2;
  const cy = size / 2;
  const radius = size / 2 - 8;
  const lineWidth = 6;
  const startAngle = -Math.PI / 2;
  const endAngle = startAngle + (pct / 100) * Math.PI * 2;

  // Background ring
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.strokeStyle = '#333';
  ctx.lineWidth = lineWidth;
  ctx.stroke();

  // Progress arc
  ctx.beginPath();
  ctx.arc(cx, cy, radius, startAngle, endAngle);
  ctx.strokeStyle = pct >= 50 ? '#44ff44' : '#ff4444';
  ctx.lineWidth = lineWidth;
  ctx.lineCap = 'round';
  ctx.stroke();
}

/** Draw a simple line chart on a canvas. */
function drawRatingChart(canvas: HTMLCanvasElement, data: number[]): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  canvas.width = rect.width * dpr;
  canvas.height = 150 * dpr;
  ctx.scale(dpr, dpr);

  const w = rect.width;
  const h = 150;
  const pad = { top: 20, right: 20, bottom: 25, left: 50 };

  if (data.length < 2) {
    ctx.fillStyle = '#444';
    ctx.font = '12px Courier New';
    ctx.textAlign = 'center';
    ctx.fillText('Not enough data', w / 2, h / 2);
    return;
  }

  const minVal = Math.min(...data) - 20;
  const maxVal = Math.max(...data) + 20;
  const range = maxVal - minVal || 1;

  const plotW = w - pad.left - pad.right;
  const plotH = h - pad.top - pad.bottom;

  // Grid lines
  ctx.strokeStyle = '#1a1a2a';
  ctx.lineWidth = 1;
  const gridSteps = 4;
  for (let i = 0; i <= gridSteps; i++) {
    const y = pad.top + (plotH / gridSteps) * i;
    ctx.beginPath();
    ctx.moveTo(pad.left, y);
    ctx.lineTo(w - pad.right, y);
    ctx.stroke();

    // Y-axis labels
    const val = Math.round(maxVal - (range / gridSteps) * i);
    ctx.fillStyle = '#444';
    ctx.font = '10px Courier New';
    ctx.textAlign = 'right';
    ctx.fillText(String(val), pad.left - 8, y + 4);
  }

  // X-axis labels
  ctx.fillStyle = '#333';
  ctx.font = '10px Courier New';
  ctx.textAlign = 'center';
  const labelInterval = Math.max(1, Math.floor(data.length / 5));
  for (let i = 0; i < data.length; i++) {
    if (i % labelInterval === 0 || i === data.length - 1) {
      const x = pad.left + (i / (data.length - 1)) * plotW;
      ctx.fillText(String(i + 1), x, h - 6);
    }
  }

  // Line
  ctx.beginPath();
  ctx.strokeStyle = ACCENT;
  ctx.lineWidth = 2;
  ctx.lineJoin = 'round';

  for (let i = 0; i < data.length; i++) {
    const x = pad.left + (i / (data.length - 1)) * plotW;
    const y = pad.top + plotH - ((data[i] - minVal) / range) * plotH;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.stroke();

  // Gradient fill under line
  const gradient = ctx.createLinearGradient(0, pad.top, 0, pad.top + plotH);
  gradient.addColorStop(0, 'rgba(233,69,96,0.2)');
  gradient.addColorStop(1, 'rgba(233,69,96,0)');

  ctx.lineTo(pad.left + plotW, pad.top + plotH);
  ctx.lineTo(pad.left, pad.top + plotH);
  ctx.closePath();
  ctx.fillStyle = gradient;
  ctx.fill();

  // Data points
  for (let i = 0; i < data.length; i++) {
    const x = pad.left + (i / (data.length - 1)) * plotW;
    const y = pad.top + plotH - ((data[i] - minVal) / range) * plotH;

    ctx.beginPath();
    ctx.arc(x, y, 3, 0, Math.PI * 2);
    ctx.fillStyle = ACCENT;
    ctx.fill();
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 1;
    ctx.stroke();
  }
}

function observeRatingChart(canvas: HTMLCanvasElement, data: number[]): void {
  if (typeof ResizeObserver === 'undefined') return;

  let lastWidth = -1;
  let lastHeight = -1;
  ratingChartResizeObserver = new ResizeObserver((entries) => {
    const rect = entries[0]?.contentRect ?? canvas.getBoundingClientRect();
    const width = Math.round(rect.width);
    const height = Math.round(rect.height);
    if (width <= 0 || height <= 0) return;
    if (width === lastWidth && height === lastHeight) return;
    lastWidth = width;
    lastHeight = height;
    drawRatingChart(canvas, data);
  });
  ratingChartResizeObserver.observe(canvas);
}

function buildAchievements(achievements: Achievement[]): HTMLElement {
  const fragment = document.createElement('div');

  const title = document.createElement('div');
  title.className = 'prof-section-title';
  title.textContent = 'ACHIEVEMENTS';
  fragment.appendChild(title);

  const grid = document.createElement('div');
  grid.className = 'prof-achievement-grid';

  for (const achievement of achievements.slice(0, 6)) {
    const card = document.createElement('div');
    card.className = `prof-achievement-card${achievement.unlocked ? '' : ' locked'}`;

    const icon = document.createElement('div');
    icon.className = 'prof-achievement-icon';
    icon.textContent = achievement.icon;
    card.appendChild(icon);

    const copy = document.createElement('div');
    const name = document.createElement('div');
    name.className = 'prof-achievement-name';
    name.textContent = achievement.name;
    copy.appendChild(name);

    const status = document.createElement('div');
    status.className = `prof-achievement-status${achievement.unlocked ? ' unlocked' : ''}`;
    status.textContent = achievement.unlocked ? 'Unlocked' : 'Locked';
    copy.appendChild(status);
    card.appendChild(copy);

    grid.appendChild(card);
  }

  fragment.appendChild(grid);
  return fragment;
}

// ── Build ───────────────────────────────────────────────────

function build(data: ProfileData): void {
  const el = ensureOverlay();
  cleanupRatingChartObserver();
  el.innerHTML = '';

  const container = document.createElement('div');
  container.className = 'prof-container';

  // ─ Header: name + rank badge + close ─
  const header = document.createElement('div');
  header.className = 'prof-header';

  const identity = document.createElement('div');
  identity.className = 'prof-identity';

  const name = document.createElement('div');
  name.className = 'prof-name';
  name.textContent = escapeHtml(data.displayName);
  identity.appendChild(name);

  const badge = createRankBadge(data.rating, getRankTier(data.rating));
  identity.appendChild(badge);

  header.appendChild(identity);

  const closeBtn = document.createElement('div');
  closeBtn.className = 'prof-close-btn';
  closeBtn.textContent = '\u2715';
  closeBtn.addEventListener('click', () => hideProfile());
  header.appendChild(closeBtn);

  container.appendChild(header);

  // ─ Stats grid ─
  const grid = document.createElement('div');
  grid.className = 'prof-stats-grid';

  // K/D
  const kd = data.deaths > 0 ? (data.kills / data.deaths).toFixed(2) : data.kills.toFixed(2);
  const kdCard = document.createElement('div');
  kdCard.className = 'prof-stat-card';
  const kdPct = data.deaths > 0 ? (data.kills / (data.kills + data.deaths)) * 100 : 100;
  kdCard.innerHTML =
    `<div class="prof-stat-label">K/D Ratio</div>` +
    `<div class="prof-stat-value">${kd}</div>` +
    `<div class="prof-stat-sub">${data.kills}K / ${data.deaths}D</div>` +
    `<div class="prof-kd-bar"><div class="prof-kd-bar-fill" style="width:${kdPct}%"></div></div>`;
  grid.appendChild(kdCard);

  // HLTV Rating
  const hltvCard = document.createElement('div');
  hltvCard.className = 'prof-stat-card';
  const hltvColor = data.hltvRating >= 1.0 ? '#44ff44' : '#ff4444';
  hltvCard.innerHTML =
    `<div class="prof-stat-label">HLTV 2.0</div>` +
    `<div class="prof-stat-value" style="color:${hltvColor}">${data.hltvRating.toFixed(2)}</div>` +
    `<div class="prof-stat-sub">Rating</div>`;
  grid.appendChild(hltvCard);

  // ADR
  const adrCard = document.createElement('div');
  adrCard.className = 'prof-stat-card';
  adrCard.innerHTML =
    `<div class="prof-stat-label">ADR</div>` +
    `<div class="prof-stat-value">${data.adr.toFixed(1)}</div>` +
    `<div class="prof-stat-sub">Avg Damage/Round</div>`;
  grid.appendChild(adrCard);

  // Win Rate (circular)
  const wrCard = document.createElement('div');
  wrCard.className = 'prof-stat-card';

  const wrLabel = document.createElement('div');
  wrLabel.className = 'prof-stat-label';
  wrLabel.textContent = 'WIN RATE';
  wrCard.appendChild(wrLabel);

  const circleWrap = document.createElement('div');
  circleWrap.className = 'prof-winrate-circle';

  const circleCanvas = document.createElement('canvas');
  circleWrap.appendChild(circleCanvas);

  const circleText = document.createElement('div');
  circleText.className = 'prof-winrate-text';
  circleText.textContent = `${data.winRate.toFixed(0)}%`;
  circleWrap.appendChild(circleText);

  wrCard.appendChild(circleWrap);

  const wrSub = document.createElement('div');
  wrSub.className = 'prof-stat-sub';
  wrSub.textContent = `${data.wins}W / ${data.losses}L`;
  wrCard.appendChild(wrSub);

  grid.appendChild(wrCard);
  container.appendChild(grid);

  const achievements = data.achievements ?? profileAchievements;
  if (achievements.length > 0) {
    container.appendChild(buildAchievements(achievements));
  }

  // ─ Rating History Chart ─
  if (data.ratingHistory.length > 0) {
    const chartTitle = document.createElement('div');
    chartTitle.className = 'prof-section-title';
    chartTitle.textContent = 'RATING HISTORY';
    container.appendChild(chartTitle);

    const chartWrap = document.createElement('div');
    chartWrap.className = 'prof-chart-wrap';

    const chartCanvas = document.createElement('canvas');
    chartCanvas.style.width = '100%';
    chartCanvas.style.height = '150px';
    chartCanvas.addEventListener('wheel', () => {
      drawRatingChart(chartCanvas, data.ratingHistory);
    }, { passive: true });
    chartWrap.appendChild(chartCanvas);

    container.appendChild(chartWrap);

    // Draw chart after append so we get correct dimensions
    requestAnimationFrame(() => {
      drawRatingChart(chartCanvas, data.ratingHistory);
      drawWinRateCircle(circleCanvas, data.winRate);
    });
    observeRatingChart(chartCanvas, data.ratingHistory);
  } else {
    // Still draw the win rate circle
    requestAnimationFrame(() => {
      drawWinRateCircle(circleCanvas, data.winRate);
    });
  }

  // ─ Weapon Stats ─
  if (data.weaponStats.length > 0) {
    const weaponTitle = document.createElement('div');
    weaponTitle.className = 'prof-section-title';
    weaponTitle.textContent = 'WEAPON STATS';
    container.appendChild(weaponTitle);

    const table = document.createElement('table');
    table.className = 'prof-weapon-table';

    const thead = document.createElement('thead');
    const hRow = document.createElement('tr');
    const wCols = [
      { label: 'Weapon', cls: '' },
      { label: 'Kills', cls: 'center' },
      { label: 'HS %', cls: 'center' },
      { label: 'Accuracy', cls: 'center' },
    ];
    for (const col of wCols) {
      const th = document.createElement('th');
      if (col.cls) th.className = col.cls;
      th.textContent = col.label;
      hRow.appendChild(th);
    }
    thead.appendChild(hRow);
    table.appendChild(thead);

    const tbody = document.createElement('tbody');
    for (const ws of data.weaponStats) {
      const row = document.createElement('tr');

      const nameTd = document.createElement('td');
      nameTd.style.cssText = 'font-weight:bold;letter-spacing:1px;text-transform:uppercase;color:#ccc;';
      nameTd.textContent = ws.weapon;
      row.appendChild(nameTd);

      const killsTd = document.createElement('td');
      killsTd.className = 'center';
      killsTd.textContent = String(ws.kills);
      row.appendChild(killsTd);

      const hsTd = document.createElement('td');
      hsTd.className = 'center';
      hsTd.style.color = ws.headshotPct >= 50 ? '#FFD700' : '#bbb';
      hsTd.textContent = `${ws.headshotPct.toFixed(1)}%`;
      row.appendChild(hsTd);

      const accTd = document.createElement('td');
      accTd.className = 'center';
      accTd.textContent = `${ws.accuracy.toFixed(1)}%`;
      row.appendChild(accTd);

      tbody.appendChild(row);
    }
    table.appendChild(tbody);
    container.appendChild(table);
  }

  // ─ Recent Matches ─
  if (data.recentMatches.length > 0) {
    const matchTitle = document.createElement('div');
    matchTitle.className = 'prof-section-title';
    matchTitle.textContent = 'RECENT MATCHES';
    container.appendChild(matchTitle);

    const matchList = document.createElement('div');
    matchList.className = 'prof-match-list';

    for (const match of data.recentMatches) {
      const row = document.createElement('div');
      row.className = 'prof-match-row';

      // Result
      const result = document.createElement('div');
      result.className = `prof-match-result ${match.result}`;
      result.textContent = match.result.toUpperCase();
      row.appendChild(result);

      // Map
      const map = document.createElement('div');
      map.className = 'prof-match-map';
      map.textContent = match.map;
      row.appendChild(map);

      // K/D/A
      const kda = document.createElement('div');
      kda.className = 'prof-match-kda';
      kda.textContent = `${match.kills} / ${match.deaths} / ${match.assists}`;
      row.appendChild(kda);

      // Rating change
      const rc = document.createElement('div');
      const isPositive = match.ratingChange >= 0;
      rc.className = `prof-match-rating ${isPositive ? 'positive' : 'negative'}`;
      rc.textContent = `${isPositive ? '+' : ''}${match.ratingChange}`;
      row.appendChild(rc);

      matchList.appendChild(row);
    }

    container.appendChild(matchList);
  }

  el.appendChild(container);
}

// ── Public API ──────────────────────────────────────────────

/** Show the player profile screen with full stats. */
export function showProfile(profileData: ProfileData): void {
  currentProfileData = profileData;
  build(profileData);
  const el = ensureOverlay();
  el.style.display = 'block';
}

/** Replace the profile achievement cards shown below the stats grid. */
export function setAchievements(achievements: Achievement[]): void {
  profileAchievements = achievements.map((achievement) => ({ ...achievement }));
  if (currentProfileData && overlay?.style.display === 'block') {
    build({ ...currentProfileData, achievements: profileAchievements });
  }
}

/** Hide the profile screen. */
export function hideProfile(): void {
  if (overlay) {
    cleanupRatingChartObserver();
    overlay.style.display = 'none';
    overlay.innerHTML = '';
  }
  currentProfileData = null;
}
